import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { TrendingUp } from 'lucide-react';

const RANGES = [8, 12, 26];

const TrendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm px-3 py-2 text-xs" data-testid="trends-tooltip">
      <p className="font-semibold text-slate-700">Week of {p.label}</p>
      <p className="text-slate-400 mb-1">
        {p.source === 'live' ? 'This week (live)' : p.source === 'snapshot' ? `Snapshot ${p.snapshot_date}` : 'No snapshot recorded'}
      </p>
      {p.source !== 'none' && <>
        <p className="text-rose-600">Overdue: {p.overdue}</p>
        <p className="text-orange-600">Blocked: {p.blocked}</p>
        <p className="text-slate-500">Open: {p.open}</p>
      </>}
    </div>
  );
};

export const WorkloadTrends = () => {
  const { api } = useAuth();
  const [key, setKey] = useState('all');
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);

  useEffect(() => {
    api().get('/rd/workload-trends', { params: { weeks, key } }).then(r => setData(r.data)).catch(() => setData(null));
  }, [api, weeks, key]);

  const recorded = (data?.weeks || []).filter(w => w.source === 'snapshot').length;

  return (
    <Card className="p-5 space-y-4" data-testid="workload-trends">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-fuchsia-600" />Trends</h2>
          <p className="text-xs text-slate-500">Weekly overdue and blocked counts, from each week's Friday snapshot (current week is live).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={key} onValueChange={setKey}>
            <SelectTrigger className="w-52" data-testid="trends-person-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All team</SelectItem>
              {(data?.people || []).map(p => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="trends-range">
            {RANGES.map(r => (
              <button
                key={r}
                onClick={() => setWeeks(r)}
                className={`px-3 py-1.5 text-xs transition-colors ${weeks === r ? 'bg-fuchsia-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid={`trends-range-${r}`}
              >
                {r}w
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="h-64" data-testid="trends-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data?.weeks || []} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
            <Tooltip content={<TrendTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="overdue" name="Overdue" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
            <Line type="monotone" dataKey="blocked" name="Blocked" stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[11px] text-slate-400" data-testid="trends-history-note">
        {data?.history_since
          ? `History recorded since ${data.history_since} · ${recorded} past week${recorded === 1 ? '' : 's'} with snapshots in this range. Snapshots are taken nightly at 11:55 PM Mountain.`
          : 'History starts building tonight — snapshots are taken nightly at 11:55 PM Mountain. Only the live current week is shown for now.'}
      </p>
    </Card>
  );
};
